/**
 * Liberty Internal Notes – matches Angular note-add + note-list + note-edit (Identify / Edit dialog).
 * Supports COMPANY (company edit) and PROPERTY (property edit) via fromPage + entityId.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, StickyNote } from "lucide-react";
import apiClient from "@/lib/api/apiClient";
import { COMM_LABEL } from "@/lib/styles/companyEditTabShell";
import { coerceApiArray } from "@/lib/utils/coerceApiArray";
import { LIBERTY_ROLES } from "@/constants/roles";
import { showError, showSuccess, formatApiError, showValidationMessage } from "@/lib/utils/notifications";
import {
    MentionTextarea,
    applyTaggingToNotes,
} from "@/components/notes/MentionTextarea";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { primaryEntityActionButtonClass } from "@/components/ui/entityActionButtonClasses";
import {
    companyEditConfirmPresentation,
    NOTES_ATTACHMENT_FILE_TYPE,
    NOTES_ATTACHMENT_MODULE,
    TRANSFER_LOG_DEFAULT_USER_FILTER_ROLES,
} from "../constants";
import { EditNoteModal } from "@/components/notes/EditNoteModal";
import { useUserRole } from "@/hooks/useUserRole";
import { generateFilePreviewUrl, isImageFile, openFilePreview } from "@/lib/utils/filePreview";
import { cn } from "@/lib/utils/helpers";
import { SectionCard } from "@/components/ui/SectionCard";
import notesStyles from "../styles/libertyInternalNotes.module.css";
import {
    formatNoteBodyForDisplay,
    isAttachedFileRemovedNote,
    isTransferAuditNote,
    shouldRenderNoteBodyAsHtml,
} from '@/features/company/utils/libertyInternalNotesDisplay'
import type {
    LibertyInternalNoteAttachment,
    LibertyInternalNoteItem,
    LibertyInternalNotesProps,
    NotesFromPage,
} from '../types';

function minutesSinceNoteCreated(created_at: number | string): number {
    let tsSec: number;
    if (typeof created_at === "number") {
        tsSec = created_at > 1e12 ? created_at / 1000 : created_at;
    } else {
        const ms = new Date(created_at).getTime();
        tsSec = Number.isNaN(ms) ? 0 : ms / 1000;
    }
    return (Date.now() / 1000 - tsSec) / 60;
}

/** Angular `note-list.filterNotes` for TRANSFER_LOG: system = [AUDIT]; user = not System author and not [AUDIT]. */
function transferLogNoteFilter(note: LibertyInternalNoteItem, filter: "all" | "system" | "user"): boolean {
    if (filter === "all") return true
    const text = note.notes || ""
    if (filter === "system") return text.startsWith("[AUDIT]")
    return note.name !== "System" && !text.startsWith("[AUDIT]")
}

function isSystemNote(note: LibertyInternalNoteItem): boolean {
    const notesText = note?.notes
    if (!notesText) return false;
    const systemPatterns = [
        'transferred from company', 'merge into property',
        'User account Inactive', 'User account re-active',
        'Added temporary password', 'Staff moved from company',
        'Staff moved from property', 'Updated company details',
        'Marked as Active in Liberty', 'Marked as Not Active in Liberty',
        'Property transferred from company',
        'Email sent to', 'Email Change:', 'Email swap',
        'An email was not sent - Invalid email address',
        'Lease date update', 'Lease Start Moved', 'Rent Changed -',
        'Checkin by', 'Checkout by', 'Checkout transfered to',
        'Applicant #', 'Separate Application :', 'Link Application :',
        'Remove Application :', 'Update Applicant status',
        'resubmitted without any application information',
        'was attempted and needs to be reviewed by',
        'Denied reason -', 'Phone Number swap',
        'User account locked', 'Resubmit Credit report',
        'Updated Property.', 'Updated About Yourself', 'Updated Income and Employement',
        'Updated property details', 'Switched',
        'Proof of Income (POI)', 'Identify other co-applicants',
        'Unreadable Forms Uploaded', 'Work History:', 'Debt:',
        'Issue: Error while pulling your credit report',
    ];
    return (
        isTransferAuditNote(notesText) ||
        systemPatterns.some(p => notesText.includes(p) || notesText.startsWith(p)) ||
        !!note.impersonated_by
    );
}

/**
 * Mirrors Angular `note-list.component.ts` `refreshNotes()` edit rules for COMPANY / PROPERTY.
 */
function computeNoteIsEditable(note: LibertyInternalNoteItem, fromPage: NotesFromPage, roleName: string, userId: number | null | undefined): boolean {
    const libertyList = LIBERTY_ROLES as readonly string[];
    if (!note?.notes || userId == null) return false;

    let isEdit = false;
    if (
        note.created_at != null &&
        minutesSinceNoteCreated(note.created_at) < 5 &&
        libertyList.includes(roleName) &&
        String(note.fk_user_id ?? "") === String(userId) &&
        !note.notes.startsWith("Risk Rating changed")
    ) {
        isEdit = true;
    }

    if ((fromPage === "COMPANY" || fromPage === "PROPERTY" || fromPage === "TRANSFER_LOG") && isSystemNote(note)) {
        return false;
    }

    const n = note.notes;
    if (
        n.startsWith("Email sent to") ||
        n.startsWith("Lease date update") ||
        n.includes("Checkin by") ||
        n.includes("Checkout by") ||
        n.includes("Checkout transfered to") ||
        n.includes("Denied reason -") ||
        n.startsWith("Email Change:") ||
        n.startsWith("Applicant #") ||
        n.startsWith("Email swap") ||
        n.startsWith("Lease Start Moved") ||
        !!note.impersonated_by ||
        n.startsWith("Separate Application :") ||
        n.startsWith("Link Application :") ||
        n.startsWith("Remove Application :") ||
        n.startsWith("Rent Changed -") ||
        n.startsWith("Risk Rating changed from -") ||
        n.includes("was attempted and needs to be reviewed by") ||
        n.startsWith("Phone Number swap") ||
        n.includes("resubmitted without any application information") ||
        n.startsWith("Update Applicant status") ||
        ["Updated Property.", "Updated About Yourself<br/>", "Updated Income and Employement"].includes(n) ||
        n.startsWith("Switched") ||
        n.includes("Marked as Active in Liberty") ||
        n.includes("Marked as  Not Active in Liberty") ||
        n.startsWith("Updated company details") ||
        n.startsWith("Updated property details") ||
        n.includes("transferred from company") ||
        n.includes("merge into property") ||
        n.includes("User account Inactive") ||
        n.includes("User account re-active") ||
        n.includes("Added temporary password") ||
        n.startsWith("Staff moved from company") ||
        n.startsWith("Staff moved from property") ||
        n.includes("Email swap") ||
        n.includes("Phone Number swap") ||
        n.includes("An email was not sent - Invalid email address") ||
        n.includes("User account locked") ||
        n.includes("Resubmit Credit report")
    ) {
        return false;
    }

    return isEdit;
}

function getNoteRowId(note: LibertyInternalNoteItem): string | number | null {
    if (note.notes_id != null && String(note.notes_id) !== "") return note.notes_id;
    if (note.id != null && String(note.id) !== "") return note.id;
    return null;
}

function resolveNoteAttachmentIds(att: LibertyInternalNoteAttachment): { fileId: string; refId: string } | null {
    const raw = att as Record<string, unknown>;
    const fileId = raw.id ?? raw.attachment_id;
    const refId = raw.fk_ref_id ?? raw.fk_refid ?? raw.fk_refId;
    if (fileId == null || refId == null || String(fileId) === "" || String(refId) === "") return null;
    return { fileId: String(fileId), refId: String(refId) };
}

/** All image attachment preview URLs on one note (for file-handlers prev/next). */
function buildNoteImageFilesList(note: LibertyInternalNoteItem): string[] | undefined {
    const list: string[] = [];
    for (const att of note.attachments ?? []) {
        const ids = resolveNoteAttachmentIds(att);
        if (!ids) continue;
        const nm =
            att.attachment_name != null && String(att.attachment_name).trim() !== ""
                ? String(att.attachment_name)
                : "";
        if (!isImageFile(nm)) continue;
        list.push(
            generateFilePreviewUrl(ids.fileId, NOTES_ATTACHMENT_FILE_TYPE, ids.refId, NOTES_ATTACHMENT_MODULE),
        );
    }
    return list.length > 1 ? list : undefined;
}

function formatDate(ts: number | string): string {
    if (!ts) return '';
    let d: Date;
    if (typeof ts === 'number') {
        // Unix seconds vs milliseconds (same heuristic as elsewhere in the app)
        d = new Date(ts > 1e12 ? ts : ts * 1000);
    } else {
        d = new Date(ts);
    }
    if (isNaN(d.getTime())) return String(ts);
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const yy = String(yyyy).slice(-2);
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    let hh = d.getHours();
    const mm = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12 || 12;
    return `${MM}/${dd}/${yy} ${pad(hh)}:${mm}:${ss} ${ampm}`;
}

export function LibertyInternalNotes({ fromPage, entityId, canEdit, refreshKey, embedded }: LibertyInternalNotesProps) {
    const { user, roleName } = useUserRole();
    const currentUserId = user?.user_id ?? null;

    /** Stable, page-scoped test ids (property edit uses `property-internal-notes-*`, company uses `company-internal-notes-*`). */
    const notesBaseTestId =
        fromPage === "PROPERTY"
            ? "property-internal-notes"
            : fromPage === "TRANSFER_LOG"
              ? "transfer-log-internal-notes"
              : "company-internal-notes";
    const notesConfirmIdPrefix =
        fromPage === "PROPERTY"
            ? "property-notes"
            : fromPage === "TRANSFER_LOG"
              ? "transfer-log-notes"
              : "company-notes";

    const [notes, setNotes] = useState<LibertyInternalNoteItem[]>([]);
    const [noteText, setNoteText] = useState('');
    const [fileLabel, setFileLabel] = useState('');
    const [processing, setProcessing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    /** @-mention map: `@Name` → `[userid:…]` — matches Angular note-add `taggingContent` */
    const [taggingContent, setTaggingContent] = useState<Record<string, string>>({});
    const [fileToDelete, setFileToDelete] = useState<{ fileId: string, noteIndex: number, attachIndex: number } | null>(null);
    const [editingNote, setEditingNote] = useState<LibertyInternalNoteItem | null>(null)
    /** Angular note-list: default User Notes filter for these roles when fromPage is APPLICATION / TRANSFER_LOG. */
    const [transferLogListFilter, setTransferLogListFilter] = useState<"all" | "system" | "user">("all")

    const loadNotes = useCallback(async () => {
        setProcessing(true);
        try {
            /** Angular `note-list.loadNotes`: GET `/api/notes/:id` with `{ notes_for: fromPage }`. */
            const response = await apiClient.get<unknown>(`/api/notes/${entityId}`, {
                params: { notes_for: fromPage },
            });
            setNotes(coerceApiArray<LibertyInternalNoteItem>(response.data));
        } catch (err) {
            if (import.meta.env.DEV) {
                console.error("[LibertyInternalNotes] loadNotes failed", { entityId, fromPage, err });
            }
            setNotes([]);
        } finally {
            setProcessing(false);
        }
    }, [entityId, fromPage]);

    useEffect(() => {
        if (entityId) loadNotes();
    }, [entityId, loadNotes, refreshKey]);

    useEffect(() => {
        if (fromPage !== "TRANSFER_LOG") return
        if (!roleName) return
        if ((TRANSFER_LOG_DEFAULT_USER_FILTER_ROLES as readonly string[]).includes(roleName)) {
            setTransferLogListFilter("user")
        } else {
            setTransferLogListFilter("all")
        }
    }, [fromPage, roleName])

    const displayedNotes =
        fromPage === "TRANSFER_LOG"
            ? notes.filter((n) => transferLogNoteFilter(n, transferLogListFilter))
            : notes

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !files.length) {
            setFileLabel('');
            setSelectedFiles(null);
            return;
        }
        let label = '';
        for (let i = 0; i < files.length; i++) {
            const fileSize = files[i].size / 1024 / 1024;
            if (fileSize > 5) {
                showValidationMessage('File Size Exceeds Allowable Limit of 5 MB.');
                if (fileInputRef.current) fileInputRef.current.value = '';
                setFileLabel('');
                setSelectedFiles(null);
                return;
            }
            label += (label ? ', ' : '') + files[i].name;
        }
        setFileLabel(label);
        setSelectedFiles(files);
    };

    const handleSubmit = async () => {
        if (!noteText.trim() && !fileLabel) return;

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('notes_for', fromPage);
            if (noteText.trim()) {
                const notesToSend = applyTaggingToNotes(noteText.trim(), taggingContent ?? {});
                formData.append('notes', notesToSend);
            }
            /** Angular `note-add.addNote`: attachments only for COMPANY / PROPERTY. */
            if (fromPage !== "TRANSFER_LOG" && selectedFiles) {
                for (let i = 0; i < selectedFiles.length; i++) {
                    formData.append(`attachments[${i}]`, selectedFiles[i]);
                }
            }
            await apiClient.post(`/api/notes/${entityId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showSuccess('Note Added Successfully!');
            setNoteText('');
            setTaggingContent({});
            setFileLabel('');
            setSelectedFiles(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            loadNotes();
        } catch (error: unknown) {
            showError(formatApiError(error));
        } finally {
            setSubmitting(false);
        }
    };

    /** Same entry point as applicant `openFilePreview` → `/file-handlers` for images (toolbar), new tab for PDFs. */
    const handleOpenNoteAttachment = (
        fileId: string,
        refId: string,
        fileName: string,
        filesList?: string[],
    ) => {
        void openFilePreview(fileId, NOTES_ATTACHMENT_FILE_TYPE, fileName, refId, NOTES_ATTACHMENT_MODULE, filesList);
    };

    const executeDeleteFile = async () => {
        if (!fileToDelete) return
        const { fileId } = fileToDelete
        setFileToDelete(null)
        try {
            await apiClient.delete(`/api/attachments/default/${fileId}`)
            showSuccess('Document file Removed')
            await loadNotes()
        } catch {
            showError('Failed to remove file')
        }
    }

    const handleDeleteFile = (fileId: string, noteIndex: number, attachIndex: number) => {
        setFileToDelete({ fileId, noteIndex, attachIndex });
    };

    const mentionTextareaProps = {
        rows: 5 as const,
        value: noteText,
        onChange: setNoteText,
        onTaggingAdd: (displayKey: string, useridToken: string) => {
            setTaggingContent((prev) => ({
                ...prev,
                [displayKey]: useridToken,
            }));
        },
        disabled: submitting,
        placeholder: "Type your note here… (use @ to mention Liberty users)",
        className: notesStyles.textarea,
    };

    const composerSection = canEdit ? (
        <div className={notesStyles.composerBlock}>
            {fromPage === "TRANSFER_LOG" ? (
                <>
                    <label
                        data-testid={`${notesBaseTestId}-add-notes-label-transfer`}
                        className={COMM_LABEL}
                    >
                        Add Notes
                    </label>
                    <div data-testid={`${notesBaseTestId}-composer-textarea-wrap`}>
                        <MentionTextarea {...mentionTextareaProps} />
                    </div>
                    <div className={notesStyles.submitRow}>
                        <button
                            data-testid={`${notesBaseTestId}-composer-submit-simple`}
                            type="button"
                            onClick={handleSubmit}
                            disabled={!noteText.trim() || submitting}
                            className={primaryEntityActionButtonClass}
                        >
                            {submitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className={notesStyles.composerRow}>
                        <div className={notesStyles.composerField}>
                            <label
                                data-testid={`${notesBaseTestId}-add-notes-label`}
                                className={COMM_LABEL}
                            >
                                Add Notes
                            </label>
                            <div data-testid={`${notesBaseTestId}-composer-textarea-wrap`}>
                                <MentionTextarea {...mentionTextareaProps} />
                            </div>
                        </div>
                        <div className={cn(notesStyles.composerField, "min-w-0 w-full")}>
                            <div className={notesStyles.attachmentSpacer} aria-hidden />
                            <div className={cn(notesStyles.attachmentRow, "relative")}>
                                <div
                                    className={cn(
                                        notesStyles.attachmentName,
                                        !fileLabel && notesStyles.attachmentNameEmpty,
                                    )}
                                    title={fileLabel || undefined}
                                >
                                    <span className="truncate">{fileLabel || "\u00A0"}</span>
                                </div>
                                <input
                                    data-testid={`${notesBaseTestId}-attachment-input`}
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    disabled={submitting}
                                    onChange={handleFileSelect}
                                    className={notesStyles.fileInputOverlay}
                                    aria-label="Choose files to attach"
                                />
                                <button
                                    data-testid={`${notesBaseTestId}-upload-button`}
                                    type="button"
                                    disabled={submitting}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        fileInputRef.current?.click();
                                    }}
                                    className={notesStyles.uploadBtn}
                                >
                                    {submitting ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                                            Uploading…
                                        </span>
                                    ) : (
                                        "Upload"
                                    )}
                                </button>
                            </div>
                            <div className={notesStyles.attachmentHint} role="alert">
                                Use CTRL (windows)/CMD (apple) + Click to select multiple files to upload at one time.
                            </div>
                        </div>
                    </div>
                    <div className={notesStyles.submitRow}>
                        <button
                            data-testid={`${notesBaseTestId}-composer-submit-with-attachments`}
                            type="button"
                            onClick={handleSubmit}
                            disabled={(!noteText.trim() && !fileLabel) || submitting}
                            className={primaryEntityActionButtonClass}
                        >
                            {submitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </>
            )}
        </div>
    ) : null;

    const notesListSection = (
        <div>
            <h6
                data-testid={`${notesBaseTestId}-recent-heading`}
                className={notesStyles.recentTitle}
            >
                Recent Notes
            </h6>

            {processing ? (
                <div data-testid={`${notesBaseTestId}-list-loading`} className={notesStyles.loadingWrap}>
                    <Loader2 className="h-6 w-6 animate-spin text-[rgb(21_151_255)]" />
                </div>
            ) : notes.length === 0 ? (
                <div data-testid={`${notesBaseTestId}-empty`} className={notesStyles.emptyState}>
                    No Notes
                </div>
            ) : (
                <>
                    {fromPage === "TRANSFER_LOG" && (
                        <div
                            data-testid={`${notesBaseTestId}-transfer-filter-group`}
                            className={notesStyles.transferFilterGroup}
                            role="group"
                            aria-label="Note type filter"
                        >
                            {(["all", "system", "user"] as const).map((key) => (
                                <label key={key} className={notesStyles.filterLabel}>
                                    <input
                                        data-testid={`${notesBaseTestId}-filter-radio-${key}`}
                                        type="radio"
                                        name="transfer_log_note_filter"
                                        checked={transferLogListFilter === key}
                                        onChange={() => setTransferLogListFilter(key)}
                                    />
                                    <span>
                                        {key === "all" ? "All" : key === "system" ? "System Notes" : "User Notes"}
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                    {displayedNotes.length === 0 ? (
                        <div data-testid={`${notesBaseTestId}-filtered-empty`} className={notesStyles.emptyState}>
                            No Notes
                        </div>
                    ) : (
                        <div data-testid={`${notesBaseTestId}-list`} className={notesStyles.notesList}>
                            {displayedNotes.map((note, displayIdx) => {
                                const sourceNoteIndex = notes.indexOf(note);
                                return (
                                    <div
                                        data-testid={`${notesBaseTestId}-row-${getNoteRowId(note) ?? displayIdx}`}
                                        key={String(note.notes_id ?? note.id ?? displayIdx)}
                                        className={notesStyles.noteRow}
                                    >
                                        <div className={notesStyles.noteMeta}>
                                            <div className={notesStyles.noteAuthor}>
                                                {note.name}
                                                {note.impersonated_by && (
                                                    <span className={notesStyles.noteImpersonated}>
                                                        {" "}(impersonated by {note.impersonated_by})
                                                    </span>
                                                )}
                                            </div>
                                            <div className={notesStyles.noteDate}>
                                                {formatDate(note.created_at)}
                                            </div>
                                            {note.updated_at && (
                                                <div className={notesStyles.noteModified}>
                                                    Modified {formatDate(note.updated_at)}
                                                </div>
                                            )}
                                        </div>
                                        <div className={notesStyles.noteContent}>
                                            {isAttachedFileRemovedNote(note) ? (
                                                <div
                                                    data-testid={`${notesBaseTestId}-attached-file-removed`}
                                                    className="text-rose-500/90 font-medium text-xs"
                                                >
                                                    Attached file has been removed.
                                                </div>
                                            ) : (
                                                <>
                                                    {(() => {
                                                        const body = formatNoteBodyForDisplay(note.notes, fromPage)
                                                        const asHtml = shouldRenderNoteBodyAsHtml(
                                                            note.notes,
                                                            fromPage,
                                                            isSystemNote(note),
                                                        )
                                                        return asHtml ? (
                                                            <span dangerouslySetInnerHTML={{ __html: body }} />
                                                        ) : (
                                                            <span>{body}</span>
                                                        )
                                                    })()}
                                                    {note.attachments && note.attachments.length > 0 && (
                                                        <div className={notesStyles.attachmentsWrap}>
                                                            {(() => {
                                                                const imageFilesList = buildNoteImageFilesList(note)
                                                                return note.attachments!.map((att, attIdx) => {
                                                                    const ids = resolveNoteAttachmentIds(att)
                                                                    if (!ids) return null
                                                                    const { fileId, refId } = ids
                                                                    const fileName =
                                                                        att.attachment_name != null &&
                                                                        String(att.attachment_name).trim() !== ''
                                                                            ? String(att.attachment_name)
                                                                            : 'Attachment'
                                                                    return (
                                                                        <div
                                                                            key={`${fileId}-${attIdx}`}
                                                                            className={notesStyles.attachmentItem}
                                                                        >
                                                                            <div className={notesStyles.attachmentActions}>
                                                                                <button
                                                                                    type="button"
                                                                                    data-testid={`${notesBaseTestId}-attachment-open-${fileId}`}
                                                                                    title={fileName}
                                                                                    onClick={() =>
                                                                                        handleOpenNoteAttachment(
                                                                                            fileId,
                                                                                            refId,
                                                                                            fileName,
                                                                                            imageFilesList,
                                                                                        )
                                                                                    }
                                                                                    className={notesStyles.attachmentOpenBtn}
                                                                                >
                                                                                    {fileName}
                                                                                </button>
                                                                                <a
                                                                                    href="#"
                                                                                    data-testid={`${notesBaseTestId}-attachment-remove-${fileId}`}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault()
                                                                                        handleDeleteFile(
                                                                                            fileId,
                                                                                            sourceNoteIndex,
                                                                                            attIdx,
                                                                                        )
                                                                                    }}
                                                                                    className={notesStyles.removeLink}
                                                                                >
                                                                                    Remove
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                            })()}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {canEdit &&
                                                computeNoteIsEditable(note, fromPage, roleName, currentUserId) &&
                                                getNoteRowId(note) != null && (
                                                    <a
                                                        href="#"
                                                        className={notesStyles.editLink}
                                                        data-testid={`${notesBaseTestId}-edit-link-${getNoteRowId(note)}`}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setEditingNote(note);
                                                        }}
                                                    >
                                                        Edit
                                                    </a>
                                                )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const dialogs = (
        <>
            <ConfirmDialog
                isOpen={!!fileToDelete}
                title="Remove File"
                message="Are you sure you want to remove this file?"
                primaryLabel="Remove"
                secondaryLabel="Cancel"
                {...companyEditConfirmPresentation}
                onPrimary={executeDeleteFile}
                onSecondary={() => setFileToDelete(null)}
                overlayDataTestId={`${notesConfirmIdPrefix}-remove-file-confirm-overlay`}
                primaryButtonDataTestId={`${notesConfirmIdPrefix}-remove-file-confirm-primary-button`}
                secondaryButtonDataTestId={`${notesConfirmIdPrefix}-remove-file-confirm-secondary-button`}
                closeButtonDataTestId={`${notesConfirmIdPrefix}-remove-file-confirm-close-button`}
            />

            <EditNoteModal
                isOpen={!!editingNote}
                notesId={editingNote ? getNoteRowId(editingNote) : null}
                initialNotes={editingNote?.notes ?? ""}
                onClose={() => setEditingNote(null)}
                onSaved={() => {
                    showSuccess("Note Edited Successfully!");
                    loadNotes();
                }}
                testIdPrefix={`${notesBaseTestId}-edit`}
            />
        </>
    );

    const notesBody = (
        <>
            {composerSection}
            {notesListSection}
            {dialogs}
        </>
    );

    if (embedded) {
        return (
            <div data-testid={`${notesBaseTestId}-section`} className={notesStyles.embeddedRoot}>
                <h2 data-testid={`${notesBaseTestId}-heading`} className={notesStyles.embeddedHeading}>
                    Liberty Internal Notes
                </h2>
                {notesBody}
            </div>
        );
    }

    return (
        <div className={fromPage === "COMPANY" ? notesStyles.pageWrap : undefined}>
            <SectionCard
                icon={StickyNote}
                title="Liberty Internal Notes"
                testId={`${notesBaseTestId}-section`}
                titleTestId={`${notesBaseTestId}-heading`}
            >
                {notesBody}
            </SectionCard>
        </div>
    );
}
